import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  ArrowUpDown,
  Briefcase,
  Clock,
  DollarSign,
  Plus,
  Sparkles,
  TrendingUp,
  Trophy,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { EmptyPanel, PageShell } from "@/components/interior-page";
import Loader from "@/components/ui/loader";
import { cn } from "@/lib/utils";
import BLAdvisorInsights from "@/components/dashboards/BLAdvisorInsights";
import { orgScopedPath } from "@/lib/orgDoor";
import { useAuth } from "@/contexts/AuthContext";

interface Pipeline {
  active: number;
  nda: number;
  loi: number;
  offer: number;
  closed: number;
}

interface AdvisorStats {
  total_deals: number;
  new_deals_this_month: number;
  total_asking_price: number;
  avg_asking_price: number;
  projected_commission: number;
  avg_commission_rate: number;
  earned_ytd: number;
  closed_deals_ytd: number;
  pipeline: Pipeline;
  earned_commission: number;
  pending_commission: number;
  pipeline_total_projected: number;
}

/* Gold funnel ramp (light → deep brand gold), closed = success green. Literal
   hexes: these feed `${color}bb` alpha concatenation, where var() won't work. */
const STAGES: { key: keyof Pipeline; label: string; chip: string; color: string }[] = [
  { key: "active", label: "Active", chip: "Active Listings", color: "#e6cd86" },
  { key: "nda", label: "NDA", chip: "NDAs Signed", color: "#d8b45c" },
  { key: "loi", label: "LOI", chip: "LOI Submitted", color: "#c99a2c" },
  { key: "offer", label: "Offer", chip: "Offer Stage", color: "#a9842b" },
  { key: "closed", label: "Closed", chip: "Closed ✓", color: "#33a86a" },
];

type Tone = "primary" | "emerald" | "amber" | "violet";

const TONE: Record<Tone, { iconBg: string; accent: string }> = {
  primary: { iconBg: "bg-primary/10 text-primary", accent: "bg-primary" },
  emerald: { iconBg: "bg-emerald-500/15 text-emerald-600", accent: "bg-emerald-500" },
  amber: { iconBg: "bg-amber-500/15 text-amber-600", accent: "bg-amber-500" },
  violet: { iconBg: "bg-primary/10 text-primary", accent: "bg-primary" },
};

const trim = (n: number) => n.toFixed(2).replace(/\.?0+$/, "");

/** Y-axis ticks that stay readable at any magnitude.
 *
 * The previous rule was a flat step of 2, which is fine for a pipeline of
 * single digits and breaks badly once a stage holds hundreds: 729 active
 * listings produced 366 tick labels, and because the tick column is a
 * fixed-height flex box they overflowed it and ran down the whole page.
 *
 * Instead pick a "nice" step — 1, 2 or 5 x 10ⁿ — targeting ~5 gridlines, so the
 * axis reads the same whether the tallest bar is 4 or 40,000. The step is
 * floored at 1 because this axis counts deals: fractional ticks are meaningless.
 */
function axisTicks(maxValue: number, targetTicks = 5): { top: number; ticks: number[] } {
  const max = Math.max(1, maxValue);
  const magnitude = 10 ** Math.floor(Math.log10(max / targetTicks));
  const normalised = max / targetTicks / magnitude;
  const step = Math.max(
    1,
    (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10) * magnitude,
  );
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let t = top; t >= 0; t -= step) ticks.push(t);
  return { top, ticks };
}

function fmtMoney(v: number): string {
  if (!v) return "$0";
  if (v >= 1e9) return `$${trim(v / 1e9)}B`;
  if (v >= 1e6) return `$${trim(v / 1e6)}M`;
  if (v >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 text-[0.7rem] font-bold uppercase tracking-[0.2em] text-muted-foreground">
      {children}
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  footer,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  footer: ReactNode;
  tone: Tone;
}) {
  const t = TONE[tone];
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-border/70 bg-card p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex items-start justify-between gap-3">
        <span className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
        <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", t.iconBg)}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <div className="mt-3 text-[2rem] font-black leading-none tracking-tight text-foreground">{value}</div>
      <div className="mt-3">{footer}</div>
      <span className={cn("absolute inset-x-0 bottom-0 h-1 opacity-70 transition-opacity group-hover:opacity-100", t.accent)} />
    </div>
  );
}

function CommBox({
  icon: Icon,
  value,
  label,
  className,
  iconClass,
}: {
  icon: LucideIcon;
  value: string;
  label: string;
  className: string;
  iconClass: string;
}) {
  return (
    <div className={cn("flex items-center gap-3 rounded-2xl border px-4 py-4", className)}>
      <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl", iconClass)}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-xl font-black leading-tight tracking-tight text-foreground">{value}</div>
        <div className="truncate text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

export default function BLAdvisorDashboard() {
  // Workspace links carry the user's organization prefix.
  const { user } = useAuth();
  const [stats, setStats] = useState<AdvisorStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<AdvisorStats>("/dashboard/bl-advisor")
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader className="min-h-[60vh]" />;

  if (!stats) {
    return (
      <PageShell>
        <EmptyPanel
          icon={AlertTriangle}
          title="Dashboard unavailable"
          description="Your advisor summary could not be loaded right now. Try refreshing the page."
        />
      </PageShell>
    );
  }

  const counts = STAGES.map((s) => stats.pipeline[s.key]);
  const { top, ticks } = axisTicks(Math.max(...counts, 1));

  return (
    <PageShell>
      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="public-hero-shell rounded-3xl px-6 py-7 shadow-sm sm:px-8">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-white/70 px-3 py-1 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              BL Advisor
            </span>
            <h1 className="mt-3 font-display text-3xl leading-tight tracking-tight text-foreground sm:text-4xl">
              Advisor Dashboard
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-7 text-muted-foreground">
              Your managed portfolio and live deal pipeline at a glance — from first listing to closed deal.
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <Link to={orgScopedPath("/bl-advisor/new-listing", user?.org_slug)}>
              <Button size="lg" className="rounded-full shadow-sm">
                <Plus className="h-4 w-4" />
                New listing
              </Button>
            </Link>
            <Link to={orgScopedPath("/bl-advisor/listings", user?.org_slug)}>
              <Button size="lg" variant="outline" className="rounded-full border-primary/20 bg-white/80 hover:bg-white">
                Managed listings
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Portfolio overview ────────────────────────────────────── */}
      <section>
        <SectionLabel>Portfolio Overview</SectionLabel>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            icon={Briefcase}
            tone="primary"
            label="Total Deals"
            value={String(stats.total_deals)}
            footer={
              stats.new_deals_this_month > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-bold text-emerald-600">
                  ↑ +{stats.new_deals_this_month} this month
                </span>
              ) : (
                <span className="text-xs font-semibold text-muted-foreground">No new deals this month</span>
              )
            }
          />
          <KpiCard
            icon={DollarSign}
            tone="emerald"
            label="Total Asking Price"
            value={fmtMoney(stats.total_asking_price)}
            footer={<span className="text-xs font-semibold text-muted-foreground">Avg {fmtMoney(stats.avg_asking_price)} / deal</span>}
          />
          <KpiCard
            icon={TrendingUp}
            tone="amber"
            label="Projected Commission"
            value={fmtMoney(stats.projected_commission)}
            footer={
              <span className="inline-flex rounded-full bg-amber-500/10 px-2 py-0.5 text-[0.7rem] font-bold text-amber-600">
                {trim(stats.avg_commission_rate)}% avg rate
              </span>
            }
          />
          <KpiCard
            icon={Trophy}
            tone="violet"
            label="Earned (YTD)"
            value={fmtMoney(stats.earned_ytd)}
            footer={
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-bold text-emerald-600">
                {stats.closed_deals_ytd} closed {stats.closed_deals_ytd === 1 ? "deal" : "deals"}
              </span>
            }
          />
        </div>
      </section>

      {/* ── Deal pipeline ─────────────────────────────────────────── */}
      <section>
        <SectionLabel>Deal Pipeline</SectionLabel>
        <div className="rounded-3xl border border-border/70 bg-card p-5 shadow-sm sm:p-6">
          <div className="flex items-center gap-2.5 pb-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ArrowUpDown className="h-4.5 w-4.5" />
            </span>
            <h2 className="text-lg font-bold tracking-tight text-foreground sm:text-xl">Stages of Each Deal</h2>
          </div>

          {/* Stage count chips */}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
            {STAGES.map((s) => (
              <div
                key={s.key}
                className="relative overflow-hidden rounded-2xl border border-border/60 bg-muted/20 p-4 text-center transition-colors hover:bg-muted/40"
              >
                <div className="text-2xl font-black tracking-tight text-foreground">{stats.pipeline[s.key]}</div>
                <div className="mt-1.5 flex items-center justify-center gap-1.5 text-[0.7rem] font-semibold text-muted-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.chip}
                </div>
                <span className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: s.color }} />
              </div>
            ))}
          </div>

          {/* Bar chart */}
          <div className="mt-7 flex gap-3">
            <div className="flex h-48 flex-col justify-between overflow-hidden py-1 text-[10px] font-semibold text-muted-foreground">
              {ticks.map((t) => (
                <span key={t} className="leading-none">{t}</span>
              ))}
            </div>
            <div className="relative flex-1">
              <div className="absolute inset-0 flex h-48 flex-col justify-between">
                {ticks.map((t) => (
                  <div key={t} className="border-t border-dashed border-border/50" />
                ))}
              </div>
              <div className="relative flex h-48 items-end justify-around gap-3 sm:gap-5">
                {STAGES.map((s) => {
                  const v = stats.pipeline[s.key];
                  return (
                    <div key={s.key} className="flex h-full flex-1 flex-col items-center justify-end">
                      <span className="mb-1.5 text-xs font-extrabold text-foreground">{v}</span>
                      <div
                        className="w-full max-w-[80px] rounded-t-lg shadow-sm transition-all duration-300"
                        style={{
                          height: `${(v / top) * 100}%`,
                          minHeight: v > 0 ? 6 : 0,
                          background: `linear-gradient(to top, ${s.color}, ${s.color}bb)`,
                        }}
                        title={`${s.chip}: ${v}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mt-2.5 flex gap-3 pl-7 sm:gap-5">
            {STAGES.map((s) => (
              <div key={s.key} className="flex-1 text-center text-[0.72rem] font-bold text-muted-foreground">
                {s.label}
              </div>
            ))}
          </div>

          {/* Commission summary strip */}
          <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <CommBox
              icon={Wallet}
              value={fmtMoney(stats.earned_commission)}
              // earned_commission is date_trunc('month') scoped, but the label
              // said just "Earned Commission" — so this read $0 next to the
              // Insights panel's all-time "Earned (1 closed) $180,000" and the
              // KPI row's "Earned (YTD)". Same screen, three different windows.
              label="Earned (This Month)"
              className="border-emerald-500/25 bg-emerald-500/10"
              iconClass="bg-emerald-500/15 text-emerald-600"
            />
            <CommBox
              icon={Clock}
              value={fmtMoney(stats.pending_commission)}
              label="Pending (LOI + Offer)"
              className="border-amber-500/25 bg-amber-500/10"
              iconClass="bg-amber-500/15 text-amber-600"
            />
            <CommBox
              icon={TrendingUp}
              value={fmtMoney(stats.pipeline_total_projected)}
              label="Pipeline Total Proj."
              className="border-primary/25 bg-primary/10"
              iconClass="bg-primary/15 text-primary"
            />
          </div>
        </div>
      </section>

      {/* ── Deal intelligence (insights) ──────────────────────────── */}
      <section>
        <SectionLabel>Deal Intelligence</SectionLabel>
        <BLAdvisorInsights />
      </section>
    </PageShell>
  );
}
